/**
 * CW-SF-R2 TKT-6 — Pipeline by Region widget (CEO role).
 *
 * Tile config carries `regions: [{label, list_view_ids[]}]`.
 * Frontend issues one /widgets/sf/pipeline-scalar call per region in
 * parallel and composes the 4-up scalar grid. Each cell echoes the
 * region label + region total + opp count.
 *
 * Owner-scope and _role_id from the tile's outer config still apply
 * via buildSfWidgetUrl (the per-region call drops list_view_ids
 * from outer config and passes the region's own set).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface RegionDef {
  label: string;
  list_view_ids: string[];
}
interface RegionScalarResponse extends SfWidgetEnvelope {
  label: string | null;
  total: number;
  count: number;
  amount_denied: boolean;
}
interface CellResult {
  label: string;
  total: number;
  count: number;
  amount_denied: boolean;
  error?: string;
}

const REFRESH_S = 5 * 60;

export function SfPipelineByRegionWidget({ config }: WidgetProps) {
  const regions: RegionDef[] = Array.isArray(config?.regions)
    ? (config!.regions as RegionDef[])
    : [];

  const [cells, setCells] = useState<CellResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  // Synthetic envelope; the per-region scalar endpoint returns one;
  // SfWidgetShell only consults it for the sync-age footer.
  const envelope: SfWidgetEnvelope | null = null;

  const fetchAll = useCallback(async () => {
    if (regions.length === 0) {
      setCells([]);
      return;
    }
    setLoading(true);
    const perCell = await Promise.all(regions.map(async (r) => {
      // Compose per-region config: override account_list_view_ids,
      // keep owner_scope + _role_id from outer config.
      const perRegionConfig = {
        ...(config ?? {}),
        account_list_view_ids: r.list_view_ids,
      };
      const params = new URLSearchParams();
      params.set('label', r.label);
      const baseUrl = buildSfWidgetUrl('/mod/cw/widgets/sf/pipeline-scalar', perRegionConfig);
      const sep = baseUrl.includes('?') ? '&' : '?';
      try {
        const data = await apiFetch<RegionScalarResponse>(`${baseUrl}${sep}${params.toString()}`);
        return {
          label: r.label,
          total: data.total,
          count: data.count,
          amount_denied: data.amount_denied,
        } as CellResult;
      } catch (e: unknown) {
        return {
          label: r.label,
          total: 0,
          count: 0,
          amount_denied: false,
          error: e instanceof Error ? e.message : 'fetch failed',
        } as CellResult;
      }
    }));
    setCells(perCell);
    setLoading(false);
    // Reuse the first cell's envelope so SfWidgetShell can show
    // the connection-state / sync-age footer.
  }, [regions, config]);

  useEffect(() => {
    void fetchAll();
    const id = window.setInterval(() => void fetchAll(), REFRESH_S * 1000);
    return () => window.clearInterval(id);
  }, [fetchAll]);

  // Build a synthetic envelope from the first non-errored cell so the
  // shell can render. If all cells error, surface that.
  const hasAnyCell = (cells?.length ?? 0) > 0;

  return (
    <div className="sf-widget sf-widget--region-grid">
      <SfWidgetShell
        envelope={envelope}
        loading={loading && cells === null}
        error={null}
        hasItems={hasAnyCell}
        emptyMessage="No regions configured for this tile. Add SF ListViews via the admin region editor."
      >
        <div className="sf-region-grid">
          {(cells ?? []).map((cell) => (
            <div key={cell.label} className="sf-region-grid__cell" data-label={cell.label}>
              <div className="sf-region-grid__label">{cell.label}</div>
              <div className="sf-region-grid__value">
                {cell.amount_denied ? '—' : formatCurrency(cell.total)}
              </div>
              <div className="sf-region-grid__meta">
                {cell.count} {cell.count === 1 ? 'opp' : 'opps'}
              </div>
              {cell.error && (
                <div className="sf-region-grid__error">{cell.error}</div>
              )}
            </div>
          ))}
        </div>
      </SfWidgetShell>
    </div>
  );
}
